/**
 * Box 5's four tables (spec §3.4, R16): one row per campaign, shared ad
 * format (D3 §3.4), ad set or ad (its format on a line under the name), in
 * the view's order (spend, highest first), then "รวม" over
 * exactly the shown rows. Every table reads name · Spend · Impressions (n<500)
 * · Clicks · CTR · CPC · CPM; Facebook and TikTok add Reaction · Comment ·
 * Share, and under a TikTok table a note says its Reaction counts likes. A row
 * the view marks lowSample (known impressions under 500, D1's rule) is faded
 * with "n<500"; NULL impressions are not a small sample. NULL prints "—",
 * never 0; a Reaction · Comment · Share the platform did not send prints
 * "ไม่ได้รายงาน" (rules-04 D4). Drawn by D1's OverviewTable, so a wide table
 * scrolls inside its own box (NFR-09).
 *
 * @module components/channels/BreakdownTables
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Copy } from 'lucide-react';
import { Chip, LowSample, OverviewTable, type OverviewColumn } from '@/components/overview/OverviewTable';
import { creativeNameOf, engagementText } from '@/services/creatives/creativeText';
import { fmtCount, fmtMoney, fmtPct } from '@/services/overview/overviewFormat';
import { CHANNEL_LABEL, campaignStateText, familyLabel, formatLabel, typeLabel, type TFn } from '@/services/channels/channelFormat';
import {
  hasEngagementColumns, type AdChannel, type AdGrainTable, type BreakdownMetrics, type BreakdownTable,
  type CampaignBreakdownRow, type CreativeBreakdownRow, type FormatBreakdownRow, type TargetingBreakdown,
  type TargetingBreakdownRow,
} from '@/services/channels/channelTypes';

/** A table line: one breakdown row, or "รวม" (OverviewTable has no footer, so the total is the last line). */
type Line<R> = { kind: 'row'; row: R } | { kind: 'total'; total: BreakdownMetrics };

/** How long "คัดลอกแล้ว" stays beside the ID, ms. */
const COPIED_MS = 2000;

const linesOf = <R,>(table: BreakdownTable<R>): Array<Line<R>> => [
  ...table.rows.map((row): Line<R> => ({ kind: 'row', row })),
  { kind: 'total', total: table.total },
];

const metricsOf = <R extends BreakdownMetrics>(l: Line<R>): BreakdownMetrics => (l.kind === 'row' ? l.row : l.total);

const bold = (on: boolean, node: React.ReactNode): React.ReactNode => (on ? <span className="font-semibold">{node}</span> : node);

/** Spend · Impressions (n<500) · Clicks · CTR · CPC · CPM; Facebook and TikTok add Reaction · Comment · Share. */
const metricColumns = <R extends BreakdownMetrics>(t: TFn, platform: AdChannel, currency: string | null): Array<OverviewColumn<Line<R>>> => {
  const num = (id: string, header: string, text: (m: BreakdownMetrics) => React.ReactNode): OverviewColumn<Line<R>> => ({
    id, header, numeric: true, cell: (l) => bold(l.kind === 'total', text(metricsOf(l))),
  });
  const shared = [
    num('spend', t('overview.metric.spend'), (m) => fmtMoney(m.spend, currency)),
    num('impressions', t('overview.metric.impressions'), (m) => <>{fmtCount(m.impressions)}<LowSample on={m.lowSample} /></>),
    num('clicks', t('overview.metric.clicks'), (m) => fmtCount(m.clicks)),
    num('ctr', t('overview.metric.ctr'), (m) => fmtPct(m.ctr)),
    num('cpc', t('overview.metric.cpc'), (m) => fmtMoney(m.cpc, currency)),
    num('cpm', t('overview.metric.cpm'), (m) => fmtMoney(m.cpm, currency)),
  ];
  if (!hasEngagementColumns(platform)) return shared;
  return [
    ...shared,
    num('reaction', t('channels.col.reaction'), (m) => engagementText(m.reactions, t)),
    num('comment', t('channels.col.comment'), (m) => engagementText(m.comments, t)),
    num('share', t('channels.col.share'), (m) => engagementText(m.shares, t)),
  ];
};

/** The first column: the row's own cell, or "รวม" on the total line. */
const nameColumn = <R,>(id: string, header: string, total: string, cell: (r: R) => React.ReactNode): OverviewColumn<Line<R>> => ({
  id, header, cell: (l) => (l.kind === 'row' ? cell(l.row) : <span className="font-semibold">{total}</span>),
});

/** A column with nothing to add up (the status): blank on the total line. */
const rowColumn = <R,>(id: string, header: string, cell: (r: R) => React.ReactNode): OverviewColumn<Line<R>> => ({
  id, header, cell: (l) => (l.kind === 'row' ? cell(l.row) : null),
});

interface GridProps<R> {
  table: BreakdownTable<R>;
  /** The table's own columns, before the shared metrics. */
  lead: ReadonlyArray<OverviewColumn<Line<R>>>;
  platform: AdChannel;
  currency: string | null;
}

/** The table; under a TikTok one, the note that its Reaction column counts likes (D3 spec §5). */
const BreakdownGrid = <R extends BreakdownMetrics & { key: string }>({ table, lead, platform, currency }: GridProps<R>): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      <OverviewTable
        columns={[...lead, ...metricColumns<R>(t, platform, currency)]}
        rows={linesOf(table)}
        rowKey={(l) => (l.kind === 'row' ? `row:${l.row.key}` : 'total')}
        faded={(l) => metricsOf(l).lowSample}
      />
      {platform === 'tiktok' && (
        <p data-tiktok-reaction className="mt-2 text-xs text-neutral-700">{t('channels.breakdown.tiktokReaction')}</p>
      )}
    </>
  );
};

/**
 * The buy type, or "ไม่ระบุประเภท", as a neutral chip: the type filter's word. Amber is D1's "Unclassified" alone
 * (rules-04 E1), which CampaignName adds for a name outside the convention.
 */
const TypeChip: React.FC<{ type: string | null }> = ({ type }) => {
  const { t } = useTranslation();
  return (
    <span className="ml-2 inline-block whitespace-nowrap rounded-full border border-neutral-400 px-1.5 text-[11px] text-neutral-800">
      {typeLabel(type, t)}
    </span>
  );
};

/**
 * The native ID and its copy button (FR-D2-09); "คัดลอกแล้ว" only after a copy that worked. The button is named with
 * its row's ID, so a list of buttons tells the rows apart; 24 px, so it does not crowd ↗ once that becomes a link.
 */
const CopyId: React.FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
    } catch {
      // No clipboard (plain-http UAT has none) or the browser refused: claim nothing; the ID stays selectable.
      setCopied(false);
    }
  };
  const label = t('channels.adsManager.copy', { id });
  return (
    <>
      <span className="select-all font-mono">{id}</span>
      <button
        type="button" onClick={() => void copy()} aria-label={label} title={label}
        className="inline-flex h-6 w-6 items-center justify-center rounded text-neutral-800 hover:bg-neutral-100"
      >
        <Copy aria-hidden="true" className="h-3.5 w-3.5" />
      </button>
      {copied && <span role="status" className="not-italic text-success-700">{t('channels.adsManager.copied')}</span>}
    </>
  );
};

/**
 * ↗ opens the platform's Ads Manager in a new tab once its template is confirmed (R18); until then a disabled mark says why.
 * The mark is not dimmed with element opacity (src/__tests__/textContrast.test.ts bans it): it reads as inactive by its
 * regular weight and neutral-700 beside the enabled link's semibold ink, and by the not-allowed cursor.
 */
const AdsManagerLink: React.FC<{ platform: AdChannel; url: string | null }> = ({ platform, url }) => {
  const { t } = useTranslation();
  if (url === null) {
    return <span aria-disabled="true" title={t('channels.adsManager.pending')} className="cursor-not-allowed select-none text-neutral-700">↗</span>;
  }
  const label = t('channels.adsManager.open', { channel: CHANNEL_LABEL[platform] });
  return <a href={url} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} className="font-semibold text-ink-900 hover:underline">↗</a>;
};

/**
 * Name, type chip and, when the name is outside the convention, D1 box 13's amber "Unclassified" chip, so the chip marks
 * what the naming coverage counts (rules-04 E1); the workspace, in all workspaces. Below them, when the
 * campaign has a native ID, the ID, its copy button and ↗. With no ID there is nothing to copy or to link, so no ↗ at
 * all: the "ID only" mark would promise an ID that is not on screen.
 */
const CampaignName: React.FC<{ platform: AdChannel; row: CampaignBreakdownRow }> = ({ platform, row }) => {
  const { t } = useTranslation();
  return (
    <div>
      <span>{row.name}</span>
      <TypeChip type={row.type} />
      {!row.conforms && <Chip text={t('looker.coverage.unclassified')} />}
      {row.workspaceName !== null && <span className="ml-2 text-xs text-neutral-700">{row.workspaceName}</span>}
      {row.nativeId !== null && (
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-neutral-700">
          <span>{t('channels.col.nativeId')}</span>
          <CopyId id={row.nativeId} />
          <AdsManagerLink platform={platform} url={row.adsManagerUrl} />
        </div>
      )}
    </div>
  );
};

/** A 40 px thumbnail; the placeholder when there is none or it fails to load. */
const Thumb: React.FC<{ url: string | null }> = ({ url }) => {
  const { t } = useTranslation();
  const [broken, setBroken] = React.useState(false);
  if (url === null || broken) {
    return (
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center whitespace-normal rounded bg-neutral-100 text-center text-[10px] leading-tight text-neutral-700">
        {t('channels.breakdown.noThumbnail')}
      </span>
    );
  }
  return <img src={url} alt="" width={40} height={40} loading="lazy" onError={() => setBroken(true)} className="h-10 w-10 shrink-0 rounded object-cover" />;
};

export const CampaignTable: React.FC<{ platform: AdChannel; table: BreakdownTable<CampaignBreakdownRow>; currency: string | null }> = ({ platform, table, currency }) => {
  const { t } = useTranslation();
  const lead = [
    nameColumn<CampaignBreakdownRow>('campaign', t('channels.col.campaign'), t('channels.breakdown.total'), (r) => <CampaignName platform={platform} row={r} />),
    rowColumn<CampaignBreakdownRow>('status', t('channels.col.status'), (r) => campaignStateText(r.state, t)),
  ];
  return <BreakdownGrid table={table} lead={lead} platform={platform} currency={currency} />;
};

export const FormatTable: React.FC<{ platform: AdChannel; table: AdGrainTable<FormatBreakdownRow>; currency: string | null }> = ({ platform, table, currency }) => {
  const { t } = useTranslation();
  const lead = [nameColumn<FormatBreakdownRow>('format', t('channels.col.format'), t('channels.breakdown.total'), (r) => formatLabel(r.format, t))];
  return <BreakdownGrid table={table} lead={lead} platform={platform} currency={currency} />;
};

export const TargetingTable: React.FC<{ platform: AdChannel; table: TargetingBreakdown; currency: string | null }> = ({ platform, table, currency }) => {
  const { t } = useTranslation();
  const lead = [
    nameColumn<TargetingBreakdownRow>('adSet', t('channels.col.adSet'), t('channels.breakdown.total'), (r) => (
      <>{r.name}{r.family === 'UNCLASSIFIED' && <Chip text={familyLabel(r.family, t)} />}</>
    )),
  ];
  return <BreakdownGrid table={table} lead={lead} platform={platform} currency={currency} />;
};

export const CreativeTable: React.FC<{ platform: AdChannel; table: AdGrainTable<CreativeBreakdownRow>; currency: string | null }> = ({ platform, table, currency }) => {
  const { t } = useTranslation();
  const lead = [
    nameColumn<CreativeBreakdownRow>('creative', t('channels.col.creative'), t('channels.breakdown.total'), (r) => (
      <span className="flex items-center gap-2">
        <Thumb key={r.thumbnailUrl ?? ''} url={r.thumbnailUrl} />
        <span>
          <span className="block">{creativeNameOf(r, t)}</span>
          <span data-creative-format className="block text-xs not-italic text-neutral-700">{formatLabel(r.format, t)}</span>
        </span>
      </span>
    )),
  ];
  return <BreakdownGrid table={table} lead={lead} platform={platform} currency={currency} />;
};
